import React, { useState, useEffect, useCallback, useMemo, forwardRef, useImperativeHandle, useRef } from 'react';
import { stocktakeSessionsService, beverageProductsService, siteInventoryService, siteBarPricesService } from '../services';
import api from '../services/api';
import stocktakeOfflineService from '../services/stocktakeOfflineService';
import { subcategoryComparator } from '../lib/subcategoryOrder';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { useToast } from '@/hooks/use-toast';
import {
  ShoppingCart, Loader2, Plus, Trash2, AlertTriangle, Calculator, Search, X, Filter, PackagePlus,
} from 'lucide-react';
import AddFromMasterModal from '../components/AddFromMasterModal';
import ProductPicker from '../components/ProductPicker';
import { SessionLinkedSalesBanner } from '../components/SessionLinkedSalesBanner';
import { measureTypeOf } from '../components/shared/measureType';

const formatCurrency = (val) => {
  if (val == null) return '\u00A30.00';
  return `\u00A3${Number(val).toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

export const WizardSalesPanel = forwardRef(function WizardSalesPanel({ sessionId, siteId, periodFrom, periodTo, isEditable, isOfflineMode, offlineData, vatRate, accountsSales, onAccountsSalesChange }, ref) {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [productsLoading, setProductsLoading] = useState(true);
  const [products, setProducts] = useState([]);
  const [siteProductIds, setSiteProductIds] = useState(null);
  const [dataSource, setDataSource] = useState(null);
  const [expectedTotal, setExpectedTotal] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [sitePrices, setSitePrices] = useState({});
  const [siteMeasures, setSiteMeasures] = useState({});
  const [addFromMasterOpen, setAddFromMasterOpen] = useState(false);

  const handleProductAssignedFromMaster = useCallback((product) => {
    // Refresh the site inventory list so the newly-assigned product
    // appears in the picker, then auto-add an entry row for it.
    if (!siteId || isOfflineMode) return;
    siteInventoryService.listInventory(siteId)
      .then(data => setSiteProductIds(new Set((data?.items || data || []).map(i => String(i.beverage_product_id || i.id)))))
      .catch(() => {});
  }, [siteId, isOfflineMode]);

  // iter340 v2 — offline AddFromMaster: queue the assignment and locally
  // extend siteProductIds + products so the picker shows the new product
  // immediately. The next sync will create the SiteBeverageInventory row
  // and reconcile the count sheet.
  const handleOfflineMasterAssign = useCallback(async (product) => {
    try {
      await stocktakeOfflineService.queueOfflineSiteInventoryAddition(Number(sessionId), product);
    } catch (e) {
      console.error('Failed to queue offline addition', e);
    }
    setSiteProductIds(prev => {
      const next = new Set(prev || []);
      next.add(String(product.id));
      return next;
    });
    setProducts(prev => prev.some(p => p.id === product.id) ? prev : [...prev, product]);
  }, [sessionId]);
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [selectedSubcategories, setSelectedSubcategories] = useState([]);
  const [priceUpdateQueue, setPriceUpdateQueue] = useState({}); // { productId: { price, dismissed } }
  const [renderLimit, setRenderLimit] = useState(50);
  // P1D — PLU investigative allowances keyed by product_id string.
  // Stored separately from sales entries; saves via PUT /plu-allowances
  // (scoped endpoint — never touches wastage/allowance/management rows).
  const [pluAllowances, setPluAllowances] = useState({}); // { [productId]: qty_string }
  const pluLoadedRef = useRef(false);
  const pluAutoSaveRef = useRef(null);
  const pluSaveRef = useRef(null);
  const [consumptionMap, setConsumptionMap] = useState({}); // { [productId]: { consumption_qty, stock_unit, ... } }
  // iter360b — Recipe serving info keyed by output_product_id. Populated
  // on panel mount from /api/recipes/serving-options-by-output. For each
  // recipe-output product (e.g. Postmix Diet Coke) we surface its
  // `default_serving_label` as a sale-unit option AND default new rows
  // to it — otherwise the dropdown falls through to the count/stock unit
  // (`ml`) which is wildly incorrect for post-mix sales that get rung in
  // servings at the POS.
  const [recipeServingsByOutput, setRecipeServingsByOutput] = useState({});
  // iter371 Wave 2 — Mid-period price-change segments per product.
  // Shape: { [pid]: [{ effective_from, segment_from, segment_to,
  //                    selling_price_incl_vat }, ...] }
  // A product with > 1 segment triggers the split-quantity UI below.
  const [priceSegments, setPriceSegments] = useState({});
  // Per-row, per-segment quantity entries when split is active:
  //   splitQtys[origIdx] = { '0': '12', '1': '8' }
  const [splitQtys, setSplitQtys] = useState({});
  const { toast } = useToast();
  const autoSaveRef = useRef(null);
  const doSaveRef = useRef(null);
  const initialLoadRef = useRef(true);
  const priceUpdateTimerRef = useRef({});
  const measureUpdateTimerRef = useRef({});

  // iter372 — Keyboard nav (Sales tab).
  // ↑/↓  jump between qty inputs on adjacent rows.
  // ←    jump to THIS row's measure (unit) selector.
  // →    (when focused on the unit trigger) jump back to qty.
  // Auto-focus the first qty input on mount so the auditor can start
  // typing immediately.
  const containerRef = useRef(null);
  const focusQty = (idx) => {
    const root = containerRef.current || document;
    const el = root.querySelector(`[data-sale-qty-idx="${idx}"]`);
    if (el) {
      el.focus();
      try { el.select?.(); } catch { /* number inputs may reject select */ }
      return true;
    }
    return false;
  };
  const handleQtyKeyDown = (idx) => (ev) => {
    if (ev.key === 'ArrowDown') {
      // Find the next qty input that exists in the DOM
      const root = containerRef.current || document;
      const inputs = Array.from(root.querySelectorAll('[data-sale-qty-idx]'));
      const cur = inputs.findIndex(el => el === ev.target);
      const next = inputs[cur + 1];
      if (next) { ev.preventDefault(); next.focus(); try { next.select?.(); } catch { /* */ } }
    } else if (ev.key === 'ArrowUp') {
      const root = containerRef.current || document;
      const inputs = Array.from(root.querySelectorAll('[data-sale-qty-idx]'));
      const cur = inputs.findIndex(el => el === ev.target);
      const prev = inputs[cur - 1];
      if (prev) { ev.preventDefault(); prev.focus(); try { prev.select?.(); } catch { /* */ } }
    } else if (ev.key === 'ArrowLeft') {
      // Only intercept when the caret is at the start of the value;
      // otherwise leave native left-arrow behaviour intact.
      // Note: <input type='number'> returns null for selectionStart in
      // Chromium ≥ 87, so we have to nullish-coalesce or the guard
      // mis-fires and ArrowLeft never jumps to the unit trigger.
      const t = ev.target;
      const start = t.selectionStart ?? 0;
      const end = t.selectionEnd ?? 0;
      if (start !== 0 || end !== 0) return;
      const root = containerRef.current || document;
      const trigger = root.querySelector(`[data-sale-unit-trigger="${idx}"]`);
      if (trigger) { ev.preventDefault(); trigger.focus(); }
    }
  };
  const handleUnitKeyDown = (idx) => (ev) => {
    if (ev.key === 'ArrowRight') {
      ev.preventDefault();
      focusQty(idx);
    }
  };

  // iter384c — Resolve the ml volume of a unit label for a product
  // (used to look up the matching site price). For most beverage
  // units `conversion_to_base` is already the ml volume (because the
  // canonical base unit is `ml`). Returns null when the label is not
  // recognised — caller will fall back to legacy NULL-measure pricing.
  const resolveUnitMl = useCallback((product, unitLabel) => {
    if (!product || !unitLabel) return null;
    const units = product.units || [];
    const lbl = String(unitLabel).trim().toLowerCase();
    if (!lbl) return null;
    const exact = units.find(u => (u.unit_type || '').trim().toLowerCase() === lbl);
    let u = exact;
    if (!u) {
      const prefix = `${lbl}_`;
      u = units.find(uu => (uu.unit_type || '').trim().toLowerCase().startsWith(prefix));
    }
    if (!u) return null;
    const conv = parseFloat(u.conversion_to_base);
    return isNaN(conv) || conv <= 0 ? null : conv;
  }, []);

  // iter384c — Pick the matching price for a product + chosen unit
  // label. Resolution order:
  //   1. Exact match on measure_ml derived from the unit_label.
  //   2. Legacy NULL-measure_ml price (fall-back for old data).
  //   3. Most-recent price across any measure (best effort).
  // Returns the price record or null.
  // iter389 — `productMap` MUST be declared before `resolveSitePrice` so the
  // useCallback closure can read it. Previously declared lower in the file,
  // which crashed the panel on sessions where `resolveSitePrice` was invoked
  // during the first render (TDZ ReferenceError on `productMap`).
  const productMap = useMemo(() => {
    const map = {};
    products.forEach(p => { map[p.id] = p; });
    return map;
  }, [products]);

  const resolveSitePrice = useCallback((productId, unitLabel) => {
    const bucket = sitePrices[productId];
    if (!bucket) return null;
    const prod = productMap[productId];
    const ml = resolveUnitMl(prod, unitLabel);
    if (ml != null) {
      const exact = bucket[String(ml)];
      if (exact) return exact;
    }
    if (bucket['null']) return bucket['null'];
    const anyRecent = Object.values(bucket).sort(
      (a, b) => (b.effective_from || '').localeCompare(a.effective_from || '')
    )[0];
    return anyRecent || null;
  }, [sitePrices, productMap, resolveUnitMl]);

  const load = useCallback(async () => {
    if (isOfflineMode && offlineData) {
      const pkgSales = offlineData.sales_entries || [];
      const record = await stocktakeOfflineService.loadOfflinePackage(Number(sessionId));
      const localSales = record?.local_changes?.sales_entries;
      const items = (localSales || pkgSales).map(item => ({
        ...item,
        beverage_product_id: item.beverage_product_id ? String(item.beverage_product_id) : '',
        unit_price: item.unit_price ?? item.unit_selling_price ?? '',
        quantity: item.quantity ?? '',
        value: item.value ?? '',
        notes: item.notes ?? '',
        unit_label: item.unit_label ?? '',
      }));
      setEntries(items);
      setProducts(offlineData.products || []);
      setDataSource('package');
      setLoading(false);
      return;
    }
    try {
      const res = await stocktakeSessionsService.getSales(sessionId);
      const items = (res.items || []).map(item => ({
        ...item,
        unit_price: item.unit_price ?? item.unit_selling_price ?? '',
      }));
      // iter371 Wave 2 — Consolidate per-product rows when the backend
      // has split entries (one per price segment). The wizard renders a
      // single row with per-segment quantity inputs; the doSave() path
      // re-emits two backend rows when the user re-saves.
      const grouped = new Map();  // pid → first entry seen
      const splits = {};          // [origIdx] → { '0': qty, '1': qty }
      const collapsed = [];
      items.forEach(it => {
        const pid = it.beverage_product_id ? String(it.beverage_product_id) : null;
        if (!pid) { collapsed.push(it); return; }
        const existing = grouped.get(pid);
        if (!existing) {
          grouped.set(pid, { entry: it, idx: collapsed.length });
          collapsed.push(it);
        } else {
          // Second (or later) row for same product → record as segment 2.
          const slot = splits[existing.idx] || {};
          if (slot['0'] == null) slot['0'] = String(existing.entry.quantity || '');
          slot['1'] = String(it.quantity || '');
          splits[existing.idx] = slot;
          // Update main row qty to the sum so legacy reads stay consistent.
          const main = collapsed[existing.idx];
          const sum = (parseFloat(main.quantity || 0) + parseFloat(it.quantity || 0));
          main.quantity = sum ? String(sum) : main.quantity;
          main._has_split = true;
        }
      });
      setEntries(collapsed);
      setSplitQtys(splits);
      setDataSource('live-api');
    } catch { /* ignore */ } finally { setLoading(false); }
  }, [sessionId, isOfflineMode, offlineData]);

  useEffect(() => { load(); }, [load]);

  // P1D — Load existing plu_allowance entries on mount.
  useEffect(() => {
    if (isOfflineMode) { pluLoadedRef.current = true; return; }
    let cancelled = false;
    stocktakeSessionsService.getAdjustments(sessionId, 'plu_allowance')
      .then(res => {
        if (cancelled) return;
        const map = {};
        (res.items || []).forEach(item => {
          if (item.beverage_product_id && item.quantity != null) {
            map[String(item.beverage_product_id)] = String(item.quantity);
          }
        });
        setPluAllowances(map);
        pluLoadedRef.current = true;
      })
      .catch(() => { pluLoadedRef.current = true; });
    return () => { cancelled = true; };
  }, [sessionId, isOfflineMode]); // eslint-disable-line react-hooks/exhaustive-deps

  // iter372 — Auto-focus the first qty input when the panel becomes
  // ready, so the auditor lands directly on the data-entry column.
  // Re-runs on `loading` transitions so a re-load doesn't strand focus.
  const focusedOnceRef = useRef(false);
  useEffect(() => {
    if (loading || productsLoading) return;
    if (focusedOnceRef.current) return;
    const t = setTimeout(() => {
      const root = containerRef.current;
      if (!root) return;
      const first = root.querySelector('[data-sale-qty-idx]:not([disabled])');
      if (first) {
        first.focus();
        try { first.select?.(); } catch { /* */ }
        focusedOnceRef.current = true;
      }
    }, 120);
    return () => clearTimeout(t);
  }, [loading, productsLoading]);

  // Phase 1: fetch per-product consumption for Consumption / Variance columns.
  // Online: read-only diagnostic endpoint.
  // Offline: compute locally from the downloaded package (opening + deliveries − credits − closing).
  useEffect(() => {
    if (!sessionId) return;
    // Online path
    if (!isOfflineMode) {
      let cancelled = false;
      stocktakeSessionsService.getSalesConsumption(sessionId)
        .then(res => {
          if (cancelled) return;
          const map = {};
          (res.items || []).forEach(it => { map[it.beverage_product_id] = it; });
          setConsumptionMap(map);
        })
        .catch(() => {/* non-fatal — columns just show '—' */});
      return () => { cancelled = true; };
    }
    // Offline path — build consumption map from the offline package + any local count entries
    if (!offlineData) return;
    let cancelled = false;
    (async () => {
      try {
        const rec = await stocktakeOfflineService.loadOfflinePackage(Number(sessionId));
        const localCounts = rec?.local_changes?.count_entries || [];
        const localCountsByLineId = new Map(localCounts.map(c => [c.count_sheet_line_id, c]));
        const lines = offlineData.count_sheet_lines || [];
        const prodsById = new Map((offlineData.products || []).map(p => [p.id, p]));
        const getStockUnit = (p) => {
          if (!p?.units?.length) return null;
          return p.units.find(u => u.is_base_stock_unit) || p.units.find(u => u.is_default_count_unit) || p.units[0];
        };
        const convertToStock = (qty, countUnit, stockUnit) => {
          if (qty == null) return 0;
          if (!countUnit || !stockUnit || countUnit.id === stockUnit.id) return parseFloat(qty) || 0;
          const c = parseFloat(countUnit.conversion_to_base || 1);
          const s = parseFloat(stockUnit.conversion_to_base || 1);
          if (!s) return parseFloat(qty) || 0;
          return (parseFloat(qty) * c) / s;
        };
        const perProduct = {};
        // Seed per-product entries from count_sheet_lines (one per product)
        for (const line of lines) {
          const pid = line.beverage_product_id;
          if (pid == null || perProduct[pid]) continue;
          const product = prodsById.get(pid);
          const stockU = getStockUnit(product);
          perProduct[pid] = {
            beverage_product_id: pid,
            product_name: line.product_name_snapshot || product?.product_name || null,
            stock_unit: stockU?.unit_type || 'unit',
            stock_unit_id: stockU?.id || null,
            opening_qty: 0,
            deliveries_qty: 0,
            credits_qty: 0,
            closing_qty: 0,
            _product: product,
            _stockU: stockU,
          };
        }
        // Opening stock (base_quantity preferred)
        for (const o of (offlineData.opening_stock || [])) {
          const pp = perProduct[o.beverage_product_id];
          if (!pp) continue;
          const q = o.base_quantity != null ? parseFloat(o.base_quantity) : parseFloat(o.quantity || 0);
          pp.opening_qty += q || 0;
        }
        // Deliveries
        for (const d of (offlineData.deliveries || [])) {
          for (const dl of (d.lines || [])) {
            const pp = perProduct[dl.beverage_product_id];
            if (!pp) continue;
            const q = dl.stock_quantity != null ? parseFloat(dl.stock_quantity) : parseFloat(dl.quantity || 0);
            pp.deliveries_qty += q || 0;
          }
        }
        // Credits
        for (const c of (offlineData.credits || [])) {
          for (const cl of (c.lines || [])) {
            const pp = perProduct[cl.beverage_product_id];
            if (!pp) continue;
            const q = cl.stock_quantity != null ? parseFloat(cl.stock_quantity) : parseFloat(cl.quantity || 0);
            pp.credits_qty += q || 0;
          }
        }
        // Closing from count entries (local changes override package entries)
        for (const line of lines) {
          const pp = perProduct[line.beverage_product_id];
          if (!pp) continue;
          const entry = localCountsByLineId.get(line.id) || line.count_entry;
          if (!entry || entry.quantity == null) continue;
          const product = pp._product;
          let countUnit = null;
          if (entry.unit_id && product?.units) {
            countUnit = product.units.find(u => u.id === entry.unit_id);
          }
          if (!countUnit && entry.unit_label && product?.units) {
            const label = String(entry.unit_label).trim().toLowerCase();
            countUnit = product.units.find(u => (u.unit_type || '').trim().toLowerCase() === label);
          }
          const qtyStock = convertToStock(entry.quantity, countUnit, pp._stockU);
          pp.closing_qty += qtyStock || 0;
        }
        if (cancelled) return;
        const map = {};
        Object.values(perProduct).forEach(pp => {
          const { _product, _stockU, ...rest } = pp;
          map[pp.beverage_product_id] = {
            ...rest,
            consumption_qty: rest.opening_qty + rest.deliveries_qty - rest.credits_qty - rest.closing_qty,
          };
        });
        setConsumptionMap(map);
      } catch { /* non-fatal */ }
    })();
    return () => { cancelled = true; };
  }, [sessionId, isOfflineMode, offlineData, entries]);

  useEffect(() => {
    if (isOfflineMode) {
      if (offlineData) {
        const invIds = new Set((offlineData.site_inventory || []).map(i => String(i.beverage_product_id)));
        if (invIds.size > 0) setSiteProductIds(invIds);
        const measMap = {};
        for (const m of (offlineData.site_product_measures || [])) measMap[m.beverage_product_id] = m;
        setSiteMeasures(measMap);
        // iter384c — Price map is now keyed by (productId, measureMl).
        // Multiple prices per product (one per measure) are kept side
        // by side so the wizard can pick the price that matches the
        // currently selected unit_label rather than collapsing to a
        // single "latest effective" row. Legacy rows with no
        // measure_ml are stored under the key 'null' and used as a
        // fall-back when no measure-specific price exists.
        const priceMap = {};
        for (const p of (offlineData.site_product_prices || [])) {
          const pid = p.beverage_product_id;
          const key = p.measure_ml == null ? 'null' : String(parseFloat(p.measure_ml));
          if (!priceMap[pid]) priceMap[pid] = {};
          const existing = priceMap[pid][key];
          if (!existing || p.effective_from > existing.effective_from) {
            priceMap[pid][key] = p;
          }
        }
        setSitePrices(priceMap);
        setProductsLoading(false);
      }
      return;
    }
    beverageProductsService.listAll({})  // iter360 — no status filter; site-inventory gate still applies
      .then(data => { setProducts(Array.isArray(data) ? data : (data?.items || data?.products || [])); setProductsLoading(false); })
      .catch(() => setProductsLoading(false));
    // iter360b — Pull the recipe-serving map (lightweight; one call per
    // panel load). Failure is non-fatal — the dropdown just falls back
    // to the legacy count-unit default if recipes can't be resolved.
    api.get('/recipes/serving-options-by-output')
      .then(r => setRecipeServingsByOutput(r?.data || {}))
      .catch(() => setRecipeServingsByOutput({}));
    if (siteId) {
      siteInventoryService.listInventory(siteId)
        .then(data => setSiteProductIds(new Set((data?.items || data || []).map(i => String(i.beverage_product_id || i.id)))))
        .catch(() => setSiteProductIds(new Set()));
      siteInventoryService.getProductMeasures(siteId)
        .then(data => { const map = {}; for (const m of (data || [])) map[m.beverage_product_id] = m; setSiteMeasures(map); })
        .catch(() => {});
      siteInventoryService.getProductPrices(siteId)
        .then(data => {
          // iter384c — key by (product, measure_ml) — see comment above.
          const map = {};
          for (const p of (data || [])) {
            const pid = p.beverage_product_id;
            const key = p.measure_ml == null ? 'null' : String(parseFloat(p.measure_ml));
            if (!map[pid]) map[pid] = {};
            const existing = map[pid][key];
            if (!existing || p.effective_from > existing.effective_from) {
              map[pid][key] = p;
            }
          }
          setSitePrices(map);
        }).catch(() => {});
    }
  }, [isOfflineMode, siteId, offlineData]);

  // iter371 Wave 2 — Pull effective price segments for the audit period
  // so the Sales tab can render two-column splits where prices change
  // mid-period. Falls back to silent failure (no splits shown) on error.
  useEffect(() => {
    if (isOfflineMode || !siteId || !periodFrom || !periodTo) return;
    siteBarPricesService.getEffectiveSegments(siteId, {
      periodFrom, periodTo, auditLocationId: null,
    })
      .then(res => {
        const map = {};
        (res?.products || []).forEach(p => {
          if (Array.isArray(p.segments) && p.segments.length > 0) {
            map[p.beverage_product_id] = p.segments;
          }
        });
        setPriceSegments(map);
      })
      .catch(() => setPriceSegments({}));
  }, [isOfflineMode, siteId, periodFrom, periodTo]);

  const siteFilteredProducts = useMemo(() => {
    // Wait for the site's own inventory; never fall back to the whole organisation catalogue online.
    if (!siteProductIds) return isOfflineMode ? products : [];
    return products.filter(p => siteProductIds.has(String(p.id)));
  }, [products, siteProductIds, isOfflineMode]);

  // iter340 v6 — Always expose ALL products assigned to the site, even when
  // returning to the Sales step after entering some sales (and even when
  // navigating away and back). Previously we early-returned when entries
  // existed, which made products without sales disappear after re-load.
  // New behaviour: keep any existing entries (preserving their data),
  // append empty rows for any assigned products not yet in the list,
  // then sort the combined list alphabetically by product name.
  useEffect(() => {
    if (loading || !siteFilteredProducts.length) return;
    setEntries(prev => {
      const existingPids = new Set((prev || []).map(e => String(e.beverage_product_id)));
      const additions = siteFilteredProducts
        .filter(p => !existingPids.has(String(p.id)))
        .map(p => {
          const siteMeasure = siteMeasures[p.id];
          const unitLabel = siteMeasure?.measure_label?.toLowerCase() || getDefaultUnit(p);
          // iter384c — pick the price for the measure we're about to
          // surface, not "any latest" price for the product.
          const sitePrice = resolveSitePrice(p.id, unitLabel);
          return {
            beverage_product_id: String(p.id),
            product_name: p.product_name,
            product_code: p.product_code || '',
            quantity: '0',
            unit_label: unitLabel,
            unit_price: sitePrice?.selling_price_incl_vat != null ? sitePrice.selling_price_incl_vat : '',
            value: '',
            notes: '',
            _price_from_site: !!sitePrice,
            _key: p.id,
          };
        });
      if (additions.length === 0 && (prev || []).length > 0) return prev;
      const merged = [...(prev || []), ...additions];
      // Hydrate product_name on existing entries that may be missing it.
      const productById = {};
      siteFilteredProducts.forEach(p => { productById[String(p.id)] = p; });
      merged.forEach(e => {
        if (!e.product_name) {
          const p = productById[String(e.beverage_product_id)];
          if (p) {
            e.product_name = p.product_name;
            e.product_code = p.product_code || '';
          }
        }
      });
      merged.sort((a, b) => (a.product_name || '').localeCompare(b.product_name || ''));
      return merged;
    });
  }, [loading, siteFilteredProducts, siteMeasures, sitePrices]); // eslint-disable-line react-hooks/exhaustive-deps

  // Available categories and subcategories
  const availableCategories = useMemo(() => {
    const cats = new Set();
    siteFilteredProducts.forEach(p => { if (p.category) cats.add(typeof p.category === 'object' ? p.category.value || p.category : p.category); });
    return [...cats].sort();
  }, [siteFilteredProducts]);

  const availableSubcategories = useMemo(() => {
    const subs = new Set();
    siteFilteredProducts.forEach(p => { if (p.subcategory) subs.add(p.subcategory); });
    // Use canonical display order, NOT alphabetical (ST-0028 fix)
    return [...subs].sort(subcategoryComparator);
  }, [siteFilteredProducts]);

  const activeFilterCount = selectedCategories.length + selectedSubcategories.length;

  const computedTotal = useMemo(() =>
    entries.reduce((sum, e) => sum + (parseFloat(e.value) || 0), 0),
  [entries]);
  const totalVariance = expectedTotal !== '' ? computedTotal - parseFloat(expectedTotal || 0) : null;
  const entriesWithQty = entries.filter(e => e.quantity && parseFloat(e.quantity) > 0).length;

  const getDefaultUnit = (prod) => {
    if (!prod) return '';
    // iter360b — When the product has a Recipe with output_product_id =
    // this product (e.g. Postmix Diet Coke is the till PLU), the auditor
    // is entering POS sales — they rang servings (glasses/cups), not the
    // count/stock unit (ml). Use the recipe's `default_serving_label` so
    // the dropdown defaults to the right thing and the audit engine can
    // resolve measure_ml from the recipe.
    const recipe = recipeServingsByOutput[String(prod.id)];
    if (recipe?.default_serving_label) {
      return String(recipe.default_serving_label).toLowerCase();
    }
    if (!prod.units?.length) return '';
    // iter420 — Honour the explicit sales-unit flags set in Product
    // Master. Order of preference:
    //  1. is_default_sales_unit (the admin's explicit pick)
    //  2. Any is_sales_unit (smallest first — "shot" beats "half pint")
    //  3. Legacy heuristic (preference list of common single labels)
    // This matters for products whose count/purchase unit IS the sales
    // unit too (e.g. wine bottles sold whole) — the backfill flags the
    // bottle row is_default_sales_unit=True so it stays the default
    // here even though it's also the purchase unit.
    const defSales = prod.units.find(u => u.is_default_sales_unit);
    if (defSales) return (defSales.unit_type || '').toLowerCase();
    const flaggedSales = prod.units
      .filter(u => u.is_sales_unit)
      .sort((a, b) => (parseFloat(a.conversion_to_base) || 0) - (parseFloat(b.conversion_to_base) || 0));
    if (flaggedSales[0]) return (flaggedSales[0].unit_type || '').toLowerCase();
    // iter340 v7 — Default to a "single" measure (pint, bottle, can, shot, glass, dram, single)
    // rather than a base/larger packing unit (case, keg, box). Bar staff
    // enter sales per single by default; matching this to how the POS
    // typically reports keeps the row totals intuitive. Iterate the
    // preference list (NOT prod.units) so 'pint' beats 'glass' for a
    // draught lager that exposes both.
    const SINGLES = ['pint', 'bottle', 'can', 'shot', 'glass', 'dram', 'single', 'serving', 'measure', 'half', 'half_pint'];
    for (const pref of SINGLES) {
      const u = prod.units.find(uu => (uu.unit_type || '').toLowerCase() === pref);
      if (u) return u.unit_type;
    }
    // Fallback: the smallest unit (lowest conversion_to_base) that's not a case/keg.
    const sorted = [...prod.units].sort((a, b) =>
      (parseFloat(a.conversion_to_base) || 0) - (parseFloat(b.conversion_to_base) || 0));
    const smallest = sorted.find(u => !['case', 'keg'].includes((u.unit_type || '').toLowerCase()));
    if (smallest) return smallest.unit_type;
    // Last resort — anything that's not a case.
    const nonCase = prod.units.find(u => (u.unit_type || '').toLowerCase() !== 'case');
    if (nonCase) return nonCase.unit_type;
    return prod.units[0].unit_type;
  };

  const getConversionNote = (entry) => {
    const prod = entry.beverage_product_id ? productMap[parseInt(entry.beverage_product_id)] : null;
    if (!prod || !prod.case_size || prod.case_size <= 0) return null;
    const qty = parseFloat(entry.quantity) || 0;
    if (qty <= 0) return null;
    const caseUnit = prod.units?.find(u => u.unit_type === 'case');
    const singlesUnit = prod.units?.find(u => u.unit_type !== 'case');
    if (!caseUnit || !singlesUnit) return null;
    const selUnit = (entry.unit_label || '').toLowerCase();
    if (selUnit === 'case') return `= ${Math.round(qty * prod.case_size * 100) / 100} ${singlesUnit.unit_type}s`;
    if (selUnit === singlesUnit.unit_type.toLowerCase()) return `= ${Math.round((qty / prod.case_size) * 10000) / 10000} cases`;
    return null;
  };

  const updateRow = (idx, field, val) => {
    setEntries(prev => prev.map((e, i) => {
      if (i !== idx) return e;
      const updated = { ...e, [field]: val };

      if (field === 'beverage_product_id' && val) {
        const pid = parseInt(val);
        const prod = productMap[pid];
        const siteMeasure = siteMeasures[pid];
        if (siteMeasure?.measure_label) updated.unit_label = siteMeasure.measure_label.toLowerCase();
        else if (prod) updated.unit_label = getDefaultUnit(prod);
        // iter384c — resolve price by the chosen unit's measure_ml.
        const sitePrice = resolveSitePrice(pid, updated.unit_label);
        if (sitePrice) {
          updated.unit_price = sitePrice.selling_price_incl_vat;
          updated._price_from_site = true;
        }
      }

      // iter384c — When the user swaps the unit_label, re-pick the
      // price for the new measure. Without this the wizard kept the
      // (e.g.) bottle-price applied even after the auditor switched
      // the line to a shot serving.
      if (field === 'unit_label' && e.beverage_product_id) {
        const pid = parseInt(e.beverage_product_id);
        const sitePrice = resolveSitePrice(pid, val);
        if (sitePrice) {
          updated.unit_price = sitePrice.selling_price_incl_vat;
          updated._price_from_site = true;
        }
        // Check 5 — Persist the new measure to Site Setup (debounced).
        if (measureUpdateTimerRef.current[pid]) clearTimeout(measureUpdateTimerRef.current[pid]);
        measureUpdateTimerRef.current[pid] = setTimeout(() => {
          handleMeasureUpdate(pid, val);
        }, 2000);
      }

      // #8: When user changes selling price, queue Site Inventory update
      if (field === 'unit_price' && e.beverage_product_id) {
        const pid = parseInt(e.beverage_product_id);
        const existingSitePrice = resolveSitePrice(pid, updated.unit_label);
        const existingPrice = existingSitePrice?.selling_price_incl_vat;
        const newPrice = parseFloat(val);
        if (!isNaN(newPrice) && newPrice > 0 && newPrice !== existingPrice) {
          // Auto-update site inventory (non-disruptive — user can dismiss)
          setPriceUpdateQueue(prev => ({ ...prev, [pid]: { price: newPrice, dismissed: false, unit_label: updated.unit_label } }));
          // Debounce the actual API call
          if (priceUpdateTimerRef.current[pid]) clearTimeout(priceUpdateTimerRef.current[pid]);
          priceUpdateTimerRef.current[pid] = setTimeout(() => {
            handlePriceUpdate(pid, newPrice, updated.unit_label);
          }, 3000);
        }
        updated._price_from_site = false; // Mark as user-edited
      }

      if (field === 'quantity' || field === 'unit_price') {
        const qty = parseFloat(field === 'quantity' ? val : updated.quantity);
        const price = parseFloat(field === 'unit_price' ? val : updated.unit_price);
        if (!isNaN(qty) && !isNaN(price) && qty > 0 && price > 0) {
          updated.value = (qty * price).toFixed(2);
        }
      }
      return updated;
    }));
  };

  // #8: Update selling price in Site Inventory
  const handlePriceUpdate = async (productId, newPrice, unitLabel) => {
    if (isOfflineMode) return; // Can't update site inventory offline
    // Note: dismissal is handled by clearTimeout in dismissPriceUpdate before this fires.
    // No stale-state check needed here.
    try {
      const prod = productMap[productId];
      const measureMl = resolveUnitMl(prod, unitLabel);
      const payload = {
        selling_price_incl_vat: newPrice,
        effective_from: new Date().toISOString().split('T')[0],
      };
      // iter384c — Persist the measure (ml) alongside the price so
      // subsequent lookups can match the price to the correct unit.
      if (measureMl != null) payload.measure_ml = measureMl;
      await siteInventoryService.createProductPrice(siteId, productId, payload);
      // Update local cache — keyed by (productId, measureMl).
      setSitePrices(prev => {
        const next = { ...prev };
        const bucket = { ...(next[productId] || {}) };
        const key = measureMl == null ? 'null' : String(measureMl);
        bucket[key] = {
          ...(bucket[key] || {}),
          beverage_product_id: productId,
          measure_ml: measureMl,
          selling_price_incl_vat: newPrice,
          effective_from: payload.effective_from,
        };
        next[productId] = bucket;
        return next;
      });
      setPriceUpdateQueue(prev => { const n = { ...prev }; delete n[productId]; return n; });
    } catch (err) {
      console.warn('Failed to update site price:', err);
    }
  };

  // Check 5 — When the auditor changes the serving measure (unit_label)
  // in the Sales panel, persist the change to Site Setup so that the next
  // load of the wizard and Site Inventory already shows the new default.
  // Debounced 2 s after the last change to avoid hammering the API.
  const handleMeasureUpdate = useCallback(async (productId, newUnitLabel) => {
    if (isOfflineMode) return;
    const prod = productMap[productId];
    const ml = resolveUnitMl(prod, newUnitLabel);
    const payload = { measure_label: newUnitLabel };
    if (ml != null) payload.measure_ml = ml;
    try {
      await siteInventoryService.upsertProductMeasure(siteId, productId, payload);
      setSiteMeasures(prev => ({
        ...prev,
        [productId]: { ...(prev[productId] || {}), measure_label: newUnitLabel, measure_ml: ml },
      }));
    } catch (err) {
      console.warn('Failed to persist measure change:', err);
    }
  }, [isOfflineMode, siteId, productMap, resolveUnitMl]);

  const dismissPriceUpdate = (productId) => {
    if (priceUpdateTimerRef.current[productId]) clearTimeout(priceUpdateTimerRef.current[productId]);
    setPriceUpdateQueue(prev => { const n = { ...prev }; delete n[productId]; return n; });
  };

  const addRow = () => {
    // iter340 v7 — Clearing active filters / search ensures the newly
    // appended row is immediately visible. Without this, the row is
    // filtered out (no product, no name, no category) and the user
    // sees their click do nothing.
    setSearchTerm('');
    setSelectedCategories([]);
    setSelectedSubcategories([]);
    setEntries(prev => {
      const next = [...prev, {
        beverage_product_id: '', quantity: '', unit_price: '',
        unit_label: '', value: '', notes: '',
        _new: true, _key: `new-${Date.now()}-${Math.random()}`,
      }];
      // Bump render limit so the new (last) row is within the visible window.
      setRenderLimit(rl => Math.max(rl, next.length));
      return next;
    });
  };

  // iter360 — Add a new sales row pre-populated with a specific product
  // (used by the empty-state "Add sales row for X" suggestions).
  const addRowForProduct = useCallback((product) => {
    setSearchTerm('');
    setSelectedCategories([]);
    setSelectedSubcategories([]);
    setEntries(prev => {
      const next = [...prev, {
        beverage_product_id: String(product.id),
        product_name: product.product_name,
        product_code: product.product_code || '',
        quantity: '', unit_price: '',
        unit_label: product.stock_unit || product.count_unit || '',
        value: '', notes: '',
        _new: true, _key: `new-${Date.now()}-${Math.random()}`,
      }];
      setRenderLimit(rl => Math.max(rl, next.length));
      return next;
    });
  }, []);

  // iter364 — Add another sales row for the SAME product (allows the
  // auditor to enter multiple discrete sales for the same product, e.g.
  // bar vs lounge tabs). Pre-fills product, unit_label, unit_price from
  // the source row so the only thing left to type is the quantity.
  // Inserts the new row directly after `sourceIdx` so it sits next to
  // the row it came from.
  const addAnotherRowFor = useCallback((sourceIdx) => {
    setSearchTerm('');
    setEntries(prev => {
      const src = prev[sourceIdx];
      if (!src || !src.beverage_product_id) return prev;
      const newRow = {
        beverage_product_id: String(src.beverage_product_id),
        product_name: src.product_name,
        product_code: src.product_code || '',
        quantity: '',
        unit_price: src.unit_price != null ? src.unit_price : '',
        unit_label: src.unit_label || '',
        value: '',
        notes: '',
        _price_from_site: src._price_from_site,
        _new: true,
        _key: `split-${Date.now()}-${Math.random()}`,
      };
      const next = [...prev.slice(0, sourceIdx + 1), newRow, ...prev.slice(sourceIdx + 1)];
      setRenderLimit(rl => Math.max(rl, next.length));
      return next;
    });
  }, []);

  // iter360 — When search returns 0 hits, look for products in site
  // inventory that DON'T yet have a sales entry, matching the search.
  // NOTE: this useMemo is declared AFTER `visibleEntries` further down
  // to avoid the same TDZ pitfall fixed in iter360b for OpeningStockPanel.
  // It's spliced in just below the `visibleEntries` declaration.

  const removeRow = (idx) => {
    setEntries(prev => prev.filter((_, i) => i !== idx));
  };

  const doSave = async () => {
    try {
      const effectiveVatRate = vatRate != null ? parseFloat(vatRate) : 20;
      const payload = [];
      entries.forEach((e, origIdx) => {
        if (!e.beverage_product_id) return;
        const pid = parseInt(e.beverage_product_id);
        const segs = priceSegments[pid] || [];
        const rowSplit = splitQtys[origIdx] || null;
        const isSplit = segs.length > 1 && rowSplit
          && Object.values(rowSplit).some(v => v !== '' && parseFloat(v) > 0);
        // iter371 Wave 2 — write one backend row per segment when split.
        if (isSplit) {
          segs.forEach((seg, segIdx) => {
            const q = parseFloat(rowSplit[String(segIdx)] || 0);
            if (!q || q <= 0) return;
            const price = seg.selling_price_incl_vat;
            const value = parseFloat((q * price).toFixed(2));
            const valueExcl = parseFloat((value / (1 + effectiveVatRate / 100)).toFixed(2));
            payload.push({
              beverage_product_id: pid,
              quantity: q,
              unit_label: e.unit_label || null,
              unit_price: price,
              value,
              selling_price_incl_vat: price,
              value_incl_vat: value,
              value_excl_vat: valueExcl,
              vat_rate_used: effectiveVatRate,
              notes: e.notes || null,
              price_note: `Segment ${segIdx + 1}: ${seg.segment_from} → ${seg.segment_to} @ £${Number(price).toFixed(2)}`,
              // migration 124 — split rows are always explicitly entered
              price_is_explicit: true,
            });
          });
          return;
        }
        // Single-segment legacy path — allow explicit 0 (Check 3 fix).
        // Skip only truly unentered (empty/null) quantity fields; a user-
        // entered or system-defaulted 0 must be persisted so unentered
        // lines are materialised as 0 in the server snapshot.
        const _qStr = String(e.quantity ?? '').trim();
        const _priceStr = String(e.unit_price ?? '').trim();
        const _priceNum = parseFloat(_priceStr);
        const _hasPriceNum = !isNaN(_priceNum); // includes 0

        if (_qStr === '') {
          // No quantity — allow saving if auditor explicitly entered a price
          // (including £0) that is NOT an auto-prefill from the site book.
          // This persists an "explicit zero price" so recalculation does not
          // fall back to the site-book price for these products.
          if (!_hasPriceNum || e._price_from_site === true) return;
          payload.push({
            beverage_product_id: pid,
            quantity: null,
            unit_label: e.unit_label || null,
            unit_price: _priceNum,
            value: null,
            selling_price_incl_vat: _priceNum,
            value_incl_vat: null,
            value_excl_vat: null,
            vat_rate_used: effectiveVatRate,
            notes: e.notes || null,
            price_note: _priceNum === 0 ? 'explicit_zero_price' : 'price_only_entry',
            // migration 124 — no-qty entries reached this path only when
            // the auditor explicitly entered/confirmed a price
            price_is_explicit: true,
          });
          return;
        }
        const _qNum = parseFloat(_qStr);
        if (isNaN(_qNum) || _qNum < 0) return; // invalid / negative — omit
        const valueInclVat = e.value ? parseFloat(e.value) : null;
        const valueExclVat = valueInclVat != null ? parseFloat((valueInclVat / (1 + effectiveVatRate / 100)).toFixed(2)) : null;
        // Price-zero fix: use NaN-safe conversion so explicit £0 is sent as 0,
        // not as null (which would trigger the site-book fallback in recalculation).
        const _pNum = parseFloat(e.unit_price);
        const priceToSend = !isNaN(_pNum) ? _pNum : null;
        payload.push({
          beverage_product_id: pid,
          quantity: _qNum,
          unit_label: e.unit_label || null,
          unit_price: priceToSend,
          value: valueInclVat,
          selling_price_incl_vat: priceToSend,
          value_incl_vat: valueInclVat,
          value_excl_vat: valueExclVat,
          vat_rate_used: effectiveVatRate,
          notes: e.notes || null,
          // migration 124 — price provenance signal from the form save path.
          // price_is_explicit tracks whether the auditor explicitly edited/
          // confirmed the PRICE (not merely entered a quantity).
          // price_is_explicit=true  → _price_from_site=false: auditor changed price.
          // price_is_explicit=false → _price_from_site=true:  price still auto-sourced
          //   from site book, even if auditor entered a quantity.
          // A scaffold price carried through a quantity edit remains auto-sourced.
          // A blank price is never explicit — it is missing input.
          price_is_explicit: priceToSend !== null && !e._price_from_site,
        });
      });
      if (isOfflineMode) {
        const result = await stocktakeOfflineService.saveLocalChanges(Number(sessionId), { sales_entries: payload });
        if (!result.success) throw new Error(result.error || 'Failed');
      } else {
        await stocktakeSessionsService.saveSales(sessionId, payload);
      }
    } catch (err) {
      toast({ title: 'Error saving sales', description: err.response?.data?.detail || err.message || 'Failed', variant: 'destructive' });
    }
  };

  useImperativeHandle(ref, () => ({ save: async () => {
    await doSaveRef.current?.();
    await pluSaveRef.current?.();
  }}));

  doSaveRef.current = doSave;

  // P1D — Save PLU investigative allowances (scoped endpoint, never
  // touches wastage/allowance/management rows).
  const savePluAllowances = async () => {
    if (!pluLoadedRef.current) return;
    const saveEntries = Object.entries(pluAllowances)
      .filter(([, qty]) => qty && parseFloat(qty) > 0)
      .map(([pid, qty]) => {
        const salesEntry = entries.find(e => String(e.beverage_product_id) === pid);
        return {
          beverage_product_id: parseInt(pid),
          quantity: parseFloat(qty),
          unit_label: salesEntry?.unit_label || 'unit',
        };
      });
    try {
      await stocktakeSessionsService.savePluAllowances(sessionId, saveEntries);
    } catch { /* non-fatal */ }
  };
  pluSaveRef.current = savePluAllowances;

  useEffect(() => {
    if (!pluLoadedRef.current) return;
    if (pluAutoSaveRef.current) clearTimeout(pluAutoSaveRef.current);
    pluAutoSaveRef.current = setTimeout(() => { pluSaveRef.current?.(); }, 2000);
    return () => { if (pluAutoSaveRef.current) clearTimeout(pluAutoSaveRef.current); };
  }, [pluAllowances]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (initialLoadRef.current) { initialLoadRef.current = false; return; }
    if (autoSaveRef.current) clearTimeout(autoSaveRef.current);
    autoSaveRef.current = setTimeout(() => { doSaveRef.current?.(); }, 2000);
    return () => { if (autoSaveRef.current) clearTimeout(autoSaveRef.current); };
  }, [entries]);

  // Cleanup timers
  useEffect(() => () => {
    Object.values(priceUpdateTimerRef.current).forEach(t => clearTimeout(t));
  }, []);

  // Filtered and sorted entries
  const visibleEntries = useMemo(() => {
    return entries.map((e, idx) => ({ ...e, _origIdx: idx })).filter(entry => {
      // iter340 v7 — A freshly-added row (no product yet) is always
      // visible regardless of filters / search; otherwise users see
      // their "Add" click do nothing while a filter is active.
      if (entry._new && !entry.beverage_product_id) return true;

      const prod = entry.beverage_product_id ? productMap[parseInt(entry.beverage_product_id)] : null;

      // Category filter
      if (selectedCategories.length > 0) {
        const cat = typeof prod?.category === 'object' ? prod?.category?.value : prod?.category;
        if (!cat || !selectedCategories.includes(cat)) return false;
      }
      // Subcategory filter
      if (selectedSubcategories.length > 0) {
        if (!prod?.subcategory || !selectedSubcategories.includes(prod.subcategory)) return false;
      }
      // Search
      if (searchTerm) {
        const q = searchTerm.toLowerCase();
        const name = (prod?.product_name || entry.product_name || '').toLowerCase();
        const code = (prod?.product_code || entry.product_code || '').toLowerCase();
        if (!name.includes(q) && !code.includes(q)) return false;
      }
      return true;
    });
  }, [entries, productMap, selectedCategories, selectedSubcategories, searchTerm]);

  // iter360 — Suggest site-inventory products matching the search but
  // not yet in entries. Declared AFTER `visibleEntries` to dodge TDZ.
  const missingFromSalesSuggestions = useMemo(() => {
    if (!searchTerm || visibleEntries.length > 0) return [];
    const q = searchTerm.toLowerCase();
    const inEntries = new Set(entries.map(e => String(e.beverage_product_id)).filter(Boolean));
    return products.filter(p => {
      if (inEntries.has(String(p.id))) return false;
      if (siteProductIds && siteProductIds.size > 0 && !siteProductIds.has(String(p.id))) return false;
      const name = (p.product_name || '').toLowerCase();
      const code = (p.product_code || '').toLowerCase();
      return name.includes(q) || code.includes(q);
    }).slice(0, 8);
  }, [searchTerm, visibleEntries, entries, products, siteProductIds]);

  // iter364 — Running totals per product in STOCK units, calculated
  // cumulatively across the entries list (in original index order). Used
  // by the renderer to show a "running" hint beside any row whose
  // product appears more than once. Math mirrors the audit engine:
  //   qty_in_stock = qty × (sale_unit.conversion_to_base / stock_unit.conversion_to_base)
  // Falls back to raw qty when no matching unit is found.
  const runningStockByRowIdx = useMemo(() => {
    const stockUnitOf = (prod) => {
      if (!prod?.units?.length) return null;
      return prod.units.find(u => u.is_base_stock_unit)
        || prod.units.find(u => u.is_default_count_unit)
        || prod.units[0];
    };
    const productCounts = new Map();   // pid → number of rows
    const runningByPid = new Map();    // pid → cumulative stock qty
    const out = new Map();             // origIdx → { running, stockUnit, productCount }
    // First pass — how many rows per product
    entries.forEach(e => {
      const pid = e.beverage_product_id ? String(e.beverage_product_id) : null;
      if (!pid) return;
      productCounts.set(pid, (productCounts.get(pid) || 0) + 1);
    });
    // Second pass — accumulate
    entries.forEach((e, idx) => {
      const pid = e.beverage_product_id ? String(e.beverage_product_id) : null;
      if (!pid) return;
      const prod = productMap[parseInt(pid)];
      const stockUnit = stockUnitOf(prod);
      const sourceUnit = prod?.units?.find(u =>
        (u.unit_type || '').toLowerCase() === (e.unit_label || '').toLowerCase()
      );
      const qty = parseFloat(e.quantity) || 0;
      let qtyInStock = qty;
      if (sourceUnit && stockUnit && sourceUnit.id !== stockUnit.id) {
        const src = parseFloat(sourceUnit.conversion_to_base) || 1;
        const stk = parseFloat(stockUnit.conversion_to_base) || 1;
        if (stk > 0) qtyInStock = qty * src / stk;
      }
      const prevRunning = runningByPid.get(pid) || 0;
      const newRunning = prevRunning + qtyInStock;
      runningByPid.set(pid, newRunning);
      const count = productCounts.get(pid) || 0;
      if (count > 1) {
        out.set(idx, {
          running: newRunning,
          stockUnit: stockUnit?.unit_type || 'unit',
          productCount: count,
        });
      }
    });
    return out;
  }, [entries, productMap]);

  // iter340 v7 — Group visible entries by subcategory with a sticky
  // heading row above each group; products sorted alphabetically within
  // each group. Entries with no subcategory (or freshly-added rows
  // without a product yet) fall into the pinned-last "Other" bucket.
  const groupedVisibleEntries = useMemo(() => {
    const groups = new Map();
    for (const entry of visibleEntries) {
      const prod = entry.beverage_product_id ? productMap[parseInt(entry.beverage_product_id)] : null;
      const sub = (prod?.subcategory && String(prod.subcategory).trim()) || 'Other';
      if (!groups.has(sub)) groups.set(sub, []);
      groups.get(sub).push(entry);
    }
    for (const arr of groups.values()) {
      arr.sort((a, b) => {
        // _new (no product) rows sort to the bottom of their group
        if (a._new && !a.beverage_product_id && !(b._new && !b.beverage_product_id)) return 1;
        if (b._new && !b.beverage_product_id && !(a._new && !a.beverage_product_id)) return -1;
        const an = (productMap[parseInt(a.beverage_product_id)]?.product_name || a.product_name || '').toLowerCase();
        const bn = (productMap[parseInt(b.beverage_product_id)]?.product_name || b.product_name || '').toLowerCase();
        return an.localeCompare(bn);
      });
    }
    const keys = [...groups.keys()].sort(subcategoryComparator);
    return keys.map(k => ({ subcategory: k, entries: groups.get(k) }));
  }, [visibleEntries, productMap]);

  if (loading || productsLoading) return (
    <Card><CardContent className="py-8 text-center text-muted-foreground">
      <Loader2 className="h-5 w-5 animate-spin inline mr-2" />
      {loading ? 'Loading sales data...' : 'Loading products...'}
    </CardContent></Card>
  );

  return (
    <Card data-testid="wizard-sales-panel" ref={containerRef}>
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="text-base flex items-center gap-2">
            <ShoppingCart className="h-5 w-5 text-rose-600" />
            Sales Entries
            {dataSource && <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[9px] font-mono border ${dataSource === 'package' ? 'bg-amber-100 text-amber-700 border-amber-200' : 'bg-blue-100 text-blue-700 border-blue-200'}`}>{dataSource === 'package' ? 'Package/IndexedDB' : 'Live API'}</span>}
          </CardTitle>
        </div>
        <CardDescription className="text-xs">
          Enter quantities sold during the audit period{periodFrom && periodTo ? ` (${periodFrom} to ${periodTo})` : ''}.
          Selling prices are from Site Inventory — edit to update both here and in Site Inventory.
          {vatRate != null && <span className="ml-1 text-muted-foreground">(VAT: {vatRate}%)</span>}
        </CardDescription>
      </CardHeader>
      <CardContent>
        {/* iter371 — missing-prices banner: prompt to fill Site Bar Prices */}
        {!isOfflineMode && products.length > 0 && Object.keys(sitePrices || {}).length === 0 && (
          <div
            className="mb-4 rounded-md border border-amber-300 bg-amber-50 p-3 flex items-start gap-2"
            data-testid="wizard-sales-missing-prices-banner"
          >
            <ShoppingCart className="h-4 w-4 text-amber-700 mt-0.5 flex-shrink-0" />
            <div className="flex-1 text-xs text-amber-900">
              <p className="font-medium">No selling prices configured for this site.</p>
              <p>Set them in <strong>Site Setup → Site Bar Prices</strong> so they auto-fill here.</p>
            </div>
            <a
              href={`/stocktaking/site-bar-prices?site=${siteId}`}
              className="text-xs font-medium underline text-amber-900"
              data-testid="wizard-sales-bar-prices-cta"
            >
              Open Site Bar Prices →
            </a>
          </div>
        )}
        {!isOfflineMode && <SessionLinkedSalesBanner sessionId={sessionId} />}
        {/* Summary */}
        <div className="grid grid-cols-3 gap-3 mb-4">
          <div className="bg-rose-50 p-3 rounded-lg">
            <p className="text-xs text-muted-foreground">Products with Sales</p>
            <p className="text-lg font-bold text-rose-700" data-testid="sales-line-count">{entriesWithQty}</p>
          </div>
          <div className="bg-rose-50 p-3 rounded-lg">
            <p className="text-xs text-muted-foreground">Total (selling price incl. VAT)</p>
            <p className="text-lg font-bold text-rose-700" data-testid="sales-total-value">{formatCurrency(computedTotal)}</p>
          </div>
          <div className="bg-slate-50 p-3 rounded-lg">
            <p className="text-xs text-muted-foreground">Site Products</p>
            <p className="text-lg font-bold text-slate-600" data-testid="sales-site-product-count">{siteProductIds ? siteProductIds.size : entries.length}</p>
          </div>
        </div>

        {/* Phase 4 — Actual Receipts (headline driver for Revised methodology).
            Single incl-VAT figure; excl-VAT is derived from the site/org VAT rate below. */}
        {isEditable && onAccountsSalesChange && (
          <div className="mb-4 p-3 rounded-lg border-2 border-emerald-200 bg-emerald-50" data-testid="accounts-sales-card">
            <div className="flex items-center gap-2 mb-2">
              <div className="h-5 w-5 rounded bg-emerald-600 text-white flex items-center justify-center text-xs font-bold">£</div>
              <span className="text-sm font-semibold text-emerald-900">Actual Receipts</span>
              <span className="text-[10px] text-emerald-700 bg-white border border-emerald-200 px-1.5 py-0.5 rounded">Drives Result headline</span>
            </div>
            <p className="text-xs text-emerald-800 mb-2">
              Enter the total takings from the accounts system for this period, <strong>including VAT</strong>. The excl-VAT figure used by the calculation is derived automatically from the organisation's VAT rate.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
              <div>
                <label className="text-[11px] font-medium text-emerald-900">Actual Receipts (incl VAT)</label>
                <Input type="number" inputMode="decimal" step="0.01" placeholder="0.00"
                  value={accountsSales?.accounts_sales_incl_vat ?? ''}
                  onChange={e => onAccountsSalesChange({ ...accountsSales, accounts_sales_incl_vat: e.target.value })}
                  className="h-9 text-sm bg-white" data-testid="accounts-sales-incl-vat" />
              </div>
              <div>
                <label className="text-[11px] font-medium text-emerald-900">Derived excl VAT (@{(vatRate != null ? parseFloat(vatRate) : 20).toFixed(2)}%)</label>
                <div
                  className="h-9 text-sm bg-slate-100 border border-slate-200 rounded-md px-3 flex items-center tabular-nums text-slate-700"
                  data-testid="accounts-sales-excl-vat-preview"
                >
                  {(() => {
                    const rawIncl = accountsSales?.accounts_sales_incl_vat;
                    const incl = rawIncl === '' || rawIncl == null ? null : parseFloat(rawIncl);
                    if (incl == null || !isFinite(incl)) return '—';
                    const rate = (vatRate != null ? parseFloat(vatRate) : 20);
                    const divisor = 1 + rate / 100;
                    if (divisor <= 0) return '—';
                    return (incl / divisor).toLocaleString('en-GB', { style: 'currency', currency: 'GBP' });
                  })()}
                </div>
              </div>
              <div>
                <label className="text-[11px] font-medium text-emerald-900">Notes (optional)</label>
                <Input type="text" placeholder="Reference / source"
                  value={accountsSales?.accounts_sales_notes ?? ''}
                  onChange={e => onAccountsSalesChange({ ...accountsSales, accounts_sales_notes: e.target.value })}
                  className="h-9 text-sm bg-white" data-testid="accounts-sales-notes" />
              </div>
            </div>
          </div>
        )}

        {/* Expected total check */}
        {isEditable && (
          <div className="mb-4 p-3 bg-slate-50 rounded-lg border border-slate-200" data-testid="sales-total-check">
            <div className="flex items-center gap-2 mb-2">
              <Calculator className="w-4 h-4 text-slate-500" />
              <span className="text-xs font-medium text-slate-600">Expected Sales Total incl. VAT (optional)</span>
            </div>
            <div className="flex items-center gap-3">
              <Input type="number" inputMode="decimal" step="0.01" placeholder="Expected total"
                value={expectedTotal} onChange={e => setExpectedTotal(e.target.value)}
                className="h-9 text-sm flex-1" data-testid="sales-expected-total-input" />
              {totalVariance !== null && (
                <div className={`text-sm font-semibold flex items-center gap-1 ${
                  Math.abs(totalVariance) < 0.01 ? 'text-green-700' :
                  Math.abs(totalVariance) < 5 ? 'text-amber-600' : 'text-red-600'
                }`} data-testid="sales-total-variance">
                  {Math.abs(totalVariance) < 0.01 ? 'Balanced' : (
                    <><AlertTriangle className="w-3.5 h-3.5" />{totalVariance > 0 ? '+' : ''}{formatCurrency(totalVariance)} variance</>
                  )}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Search bar */}
        <div className="mb-3 flex gap-2">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input value={searchTerm} onChange={e => setSearchTerm(e.target.value)}
              placeholder="Search by product name or code..." className="pl-9 h-9 text-sm" data-testid="sales-search"
              autoComplete="off" autoCorrect="off" spellCheck={false} name="sales-search-input" />
          </div>
        </div>

        {/* Category/subcategory filters */}
        {(availableCategories.length > 0 || availableSubcategories.length > 0) && (
          <div className="mb-3 space-y-2 p-2 bg-slate-50 rounded-lg border border-slate-100">
            {availableCategories.length > 0 && (
              <div>
                <p className="text-xs font-medium text-slate-500 mb-1">Category</p>
                <div className="flex flex-wrap gap-1.5">
                  {availableCategories.map(cat => {
                    const isActive = selectedCategories.includes(cat);
                    return (
                      <button key={cat}
                        onClick={() => setSelectedCategories(prev => isActive ? prev.filter(c => c !== cat) : [...prev, cat])}
                        className={`px-2.5 py-1 rounded-full text-xs font-medium transition-colors ${
                          isActive ? 'bg-rose-600 text-white' : 'bg-white text-slate-600 border border-slate-200 hover:bg-slate-100'
                        }`} data-testid={`sales-cat-${cat}`}>{cat.replace(/_/g, ' ')}</button>
                    );
                  })}
                </div>
              </div>
            )}
            {availableSubcategories.length > 0 && (
              <div>
                <p className="text-xs font-medium text-slate-500 mb-1">Sub-category</p>
                <div className="flex flex-wrap gap-1.5">
                  {availableSubcategories.map(sub => {
                    const isActive = selectedSubcategories.includes(sub);
                    return (
                      <button key={sub}
                        onClick={() => setSelectedSubcategories(prev => isActive ? prev.filter(s => s !== sub) : [...prev, sub])}
                        className={`px-2.5 py-1 rounded-full text-xs font-medium transition-colors ${
                          isActive ? 'bg-rose-600 text-white' : 'bg-white text-slate-600 border border-slate-200 hover:bg-slate-100'
                        }`} data-testid={`sales-subcat-${sub}`}>{sub}</button>
                    );
                  })}
                </div>
              </div>
            )}
            {activeFilterCount > 0 && (
              <button onClick={() => { setSelectedCategories([]); setSelectedSubcategories([]); }}
                className="text-xs text-slate-500 hover:text-slate-700 flex items-center gap-1" data-testid="sales-clear-filters">
                <X className="w-3 h-3" /> Clear filters ({activeFilterCount})
              </button>
            )}
          </div>
        )}

        {/* Column headers */}
        <div className="text-xs text-muted-foreground font-medium px-1 flex gap-2 mb-1">
          <div className="flex-[2]">Product</div>
          <div className="w-24">Unit</div>
          <div className="w-20">Qty</div>
          <div className="w-28">Selling Price</div>
          <div className="w-24">Value</div>
          <div className="w-24 text-right" title="Physical consumption derived from opening + deliveries − credits − counted closing stock.">Consumption</div>
          <div className="w-24 text-right" title="PLU qty entered − physical consumption (in stock units).">Variance</div>
          <div className="w-24 text-right text-muted-foreground" title="Investigative allowance — affects the EVT report only, not the core stocktake result.">PLU Allow.</div>
          {isEditable && <div className="w-9"></div>}
        </div>

        {/* Rows — grouped by subcategory, alphabetised within each group. */}
        <div className="space-y-1">
          {visibleEntries.length === 0 && (
            <div className="py-3" data-testid="sales-empty-state">
              <p className="text-sm text-muted-foreground text-center mb-2">
                {searchTerm || activeFilterCount > 0 ? 'No sales entries match your filters.' : 'No site products found'}
              </p>
              {/* iter360 — Suggest site-inventory products matching the search but not yet in entries */}
              {missingFromSalesSuggestions.length > 0 && (
                <div className="bg-blue-50 border border-blue-200 rounded p-3 max-w-2xl mx-auto" data-testid="missing-from-sales-suggestions">
                  <p className="text-sm text-blue-900 mb-2 font-medium">
                    The following product{missingFromSalesSuggestions.length > 1 ? 's are' : ' is'} in this site's inventory but {missingFromSalesSuggestions.length > 1 ? "haven't" : "hasn't"} been added to sales yet:
                  </p>
                  <div className="space-y-1.5">
                    {missingFromSalesSuggestions.map(p => (
                      <button
                        key={p.id}
                        onClick={() => addRowForProduct(p)}
                        className="w-full text-left px-3 py-1.5 bg-white border border-blue-200 rounded hover:bg-blue-100 hover:border-blue-400 transition flex items-center justify-between text-sm"
                        data-testid={`add-sales-missing-${p.id}-btn`}
                      >
                        <span>
                          {p.product_code && <span className="font-mono text-xs text-slate-500 mr-2">{p.product_code}</span>}
                          <span className="font-medium">{p.product_name}</span>
                          {p.subcategory && <span className="text-xs text-slate-500 ml-2">· {p.subcategory}</span>}
                        </span>
                        <span className="text-xs text-blue-700 font-medium">+ Add sales row</span>
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
          {(() => {
            // Flatten grouped entries into a render queue so we can apply
            // a single global renderLimit while still showing subcategory
            // headers above each group.
            let rendered = 0;
            const blocks = [];
            for (const group of groupedVisibleEntries) {
              if (rendered >= renderLimit) break;
              const groupRows = [];
              for (const entry of group.entries) {
                if (rendered >= renderLimit) break;
                groupRows.push(entry);
                rendered += 1;
              }
              if (groupRows.length === 0) continue;
              blocks.push({ subcategory: group.subcategory, totalCount: group.entries.length, entries: groupRows });
            }
            return blocks.map(block => (
              <div key={`grp-${block.subcategory}`} data-testid={`sales-subcategory-group-${block.subcategory.replace(/\s+/g, '-').toLowerCase()}`}>
                <div className="sticky top-0 z-[1] bg-slate-100/95 backdrop-blur px-2 py-1 border-y border-slate-200 mb-1 rounded-sm">
                  <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-600">
                    {block.subcategory}
                    <span className="ml-2 text-slate-400 normal-case font-normal">({block.totalCount})</span>
                  </p>
                </div>
                {block.entries.map(entry => {
                  const idx = entry._origIdx;
                  const prod = entry.beverage_product_id ? productMap[parseInt(entry.beverage_product_id)] : null;
                  // iter360b — Recipe-output products (e.g. Postmix Diet
                  // Coke) get their recipe servings PREPENDED to the unit
                  // list so the auditor can pick the right measure for POS
                  // sales. Without this the dropdown only shows ml/case/
                  // etc and the auditor either enters wildly wrong figures
                  // or skips the row.
                  const recipeInfo = prod ? recipeServingsByOutput[String(prod.id)] : null;
                  const recipeUnitOptions = (recipeInfo?.servings || [])
                    .filter(s => s.label)
                    .map(s => ({
                      value: String(s.label).toLowerCase(),
                      label: `${s.label}${s.ml ? ` (${s.ml}ml)` : ''}`,
                      _recipe: true,
                    }));
                  const masterUnitOptions = prod?.units?.length > 0
                    ? prod.units.map(u => ({
                        // Normalise to lower-case so values always match
                        // entry.unit_label (which the backend lower-cases on
                        // storage) regardless of how the master defines them.
                        value: (u.unit_type || '').toLowerCase(),
                        // 2026-11 fix — a mass (g/kg) or count unit's
                        // conversion_to_base is never millilitres; only a
                        // volume unit ever gets the "ml" suffix here.
                        label: u.unit_type.charAt(0).toUpperCase() + u.unit_type.slice(1) + (
                          parseFloat(u.conversion_to_base) > 1 && u.unit_type !== 'case' && measureTypeOf(u.base_unit) === 'volume'
                            ? ` ${u.conversion_to_base}ml`
                            : (measureTypeOf(u.base_unit) === 'mass' ? ` ${u.conversion_to_base}g` : '')
                        ),
                      }))
                    : [];
                  const seen = new Set();
                  const unitOptions = [...recipeUnitOptions, ...masterUnitOptions].filter(o => {
                    const key = String(o.value).toLowerCase();
                    if (seen.has(key)) return false;
                    seen.add(key);
                    return true;
                  });
                  const convNote = getConversionNote(entry);
                  const hasAutoValue = entry.quantity && entry.unit_price && parseFloat(entry.quantity) > 0 && parseFloat(entry.unit_price) > 0;
                  const pid = entry.beverage_product_id ? parseInt(entry.beverage_product_id) : null;
                  const hasPendingPriceUpdate = pid && priceUpdateQueue[pid] && !priceUpdateQueue[pid].dismissed;
                  const hasQty = entry.quantity && parseFloat(entry.quantity) > 0;
                  const isNewEmpty = !!entry._new && !entry.beverage_product_id;

                  return (
                    <div key={entry.id || entry._key || idx} data-testid={`sale-row-wrap-${idx}`}>
                    <div
                      className={`flex gap-2 items-start py-1 ${hasQty ? 'bg-rose-50/40 rounded-lg px-1' : ''} ${isNewEmpty ? 'bg-amber-50/40 rounded-lg px-1' : ''}`}
                      data-testid={`sale-row-${idx}`}>
                      <div className="flex-[2] min-w-0">
                        {(prod || entry.product_name) ? (
                          <div className="flex items-center gap-1.5 h-9">
                            <span className="text-sm truncate" data-testid={`sale-product-name-${idx}`}>
                              {(prod?.product_code || entry.product_code) && <span className="text-slate-400 text-xs">{prod?.product_code || entry.product_code}</span>}
                              {(prod?.product_code || entry.product_code) ? ' ' : ''}{prod?.product_name || entry.product_name}
                            </span>
                          </div>
                        ) : (
                          <ProductPicker
                            products={siteFilteredProducts}
                            value={entry.beverage_product_id}
                            onChange={v => updateRow(idx, 'beverage_product_id', v)}
                            disabled={!isEditable}
                            placeholder="Select product..."
                            testId={`sale-product-picker-${idx}`}
                          />
                        )}
                        {hasPendingPriceUpdate && (
                          <div className="flex items-center gap-1.5 mt-0.5 text-[10px] bg-blue-50 rounded px-1.5 py-1 border border-blue-100" data-testid={`sale-price-update-${idx}`}>
                            <span className="text-blue-600">Site Inventory price will update</span>
                            <button
                              onClick={() => dismissPriceUpdate(pid)}
                              className="px-1.5 py-0.5 rounded bg-white border border-slate-200 text-slate-600 hover:bg-red-50 hover:text-red-600 hover:border-red-200 font-medium transition-colors"
                              title="Don't update Site Inventory price"
                              data-testid={`sale-price-dismiss-${idx}`}
                            >
                              No
                            </button>
                          </div>
                        )}
                      </div>
                      <div className="w-28">
                        {unitOptions.length > 0 ? (
                          <Select value={entry.unit_label || 'none'} onValueChange={v => updateRow(idx, 'unit_label', v === 'none' ? '' : v)} disabled={!isEditable}>
                            <SelectTrigger
                              className="h-9 text-xs"
                              data-testid={`sale-unit-${idx}`}
                              data-sale-unit-trigger={idx}
                              onKeyDown={handleUnitKeyDown(idx)}
                            ><SelectValue placeholder="Unit" /></SelectTrigger>
                            <SelectContent>
                              <SelectItem value="none">--</SelectItem>
                              {unitOptions.map(o => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
                            </SelectContent>
                          </Select>
                        ) : (
                          <span className="text-xs text-muted-foreground h-9 flex items-center">{entry.unit_label || '--'}</span>
                        )}
                      </div>
                      <div className="w-20">
                        <Input type="number" step="0.01" min="0" placeholder="--"
                          inputMode="decimal"
                          value={entry.quantity || ''} onChange={e => updateRow(idx, 'quantity', e.target.value)}
                          onKeyDown={handleQtyKeyDown(idx)}
                          data-sale-qty-idx={idx}
                          disabled={!isEditable || isNewEmpty} className="h-9 text-sm" data-testid={`sale-qty-${idx}`} />
                        {convNote && <p className="text-[10px] text-blue-600 mt-0.5">{convNote}</p>}
                      </div>
                      <div className="w-28">
                        {/* iter390 — Price input is editable. Source of
                            truth is Site Bar Prices, but the wizard
                            lets the auditor override per session.
                            Saving the override also persists it back
                            to the master Site Bar Prices history (see
                            handlePriceUpdate). */}
                        <Input
                          type="number"
                          step="0.01"
                          min="0"
                          inputMode="decimal"
                          placeholder="--"
                          value={entry.unit_price != null && entry.unit_price !== '' ? entry.unit_price : ''}
                          onChange={e => updateRow(idx, 'unit_price', e.target.value)}
                          disabled={!isEditable}
                          className={`h-9 text-sm text-right tabular-nums ${entry._price_from_site ? 'bg-blue-50/40 border-blue-200' : ''}`}
                          data-testid={`sale-price-${idx}`}
                        />
                        {(entry.unit_price == null || entry.unit_price === '') && entry.beverage_product_id && (
                          <p className="text-[10px] text-amber-600 mt-0.5">
                            Set in <a
                              href={`/stocktaking/site-bar-prices?site=${siteId}`}
                              className="underline hover:text-amber-700"
                              data-testid={`sale-price-cta-${idx}`}
                            >Site Bar Prices</a>
                          </p>
                        )}
                      </div>
                      <div className="w-24">
                        <div className={`h-9 flex items-center text-sm tabular-nums px-2 rounded-md border ${hasAutoValue ? 'bg-muted border-slate-200' : 'border-transparent'}`}
                          data-testid={`sale-value-${idx}`}>
                          {hasAutoValue ? formatCurrency(parseFloat(entry.value)) : '--'}
                        </div>
                      </div>
                      {/* Phase 1 — Consumption / Variance diagnostic columns. */}
                      {(() => {
                        const cons = pid ? consumptionMap[pid] : null;
                        const stockUnitLabelRaw = cons?.stock_unit || '';
                        const rowUnitLabel = (entry.unit_label || '').toString();
                        const saleUnit = prod?.units?.find(u => (u.unit_type || '').toLowerCase() === rowUnitLabel.toLowerCase());
                        const stockUnit = prod?.units?.find(u => u.is_base_stock_unit) || prod?.units?.find(u => u.is_default_count_unit) || prod?.units?.[0];
                        // iter360e — When the row was rung in a RECIPE serving
                        // (e.g. "coke 22 fl.oz" for Postmix Diet Coke), the
                        // sale unit doesn't exist on `product.units`. Fall
                        // back to the recipe's ingredient_qty (in ml of
                        // syrup per serving) so the variance arithmetic is
                        // in serving-comparable units. Without this, the UI
                        // mixes 25 servings minus 3 litres → "22" — exactly
                        // the Forest Lodge bug report.
                        const recipeInfo = pid ? recipeServingsByOutput[String(pid)] : null;
                        const recipeServing = recipeInfo?.servings?.find(
                          s => (s.label || '').toLowerCase() === rowUnitLabel.toLowerCase()
                        ) || (recipeInfo && (recipeInfo.default_serving_label || '').toLowerCase() === rowUnitLabel.toLowerCase()
                          ? { ml: recipeInfo.default_serving_ml, label: recipeInfo.default_serving_label }
                          : null);
                        // Server-side `consumption_qty` is in product STOCK
                        // units (e.g. litres). One serving consumes
                        // `qty_in_stock_units_per_serving × box_ml` ml of
                        // ingredient — for Diet Coke that is 97.67 ml of
                        // syrup, NOT 625 ml of glass. We look that up on
                        // the loaded recipe via /api/recipes (the wizard
                        // already fetches `recipeServingsByOutput`; here we
                        // need the recipe ingredient itself, so we use the
                        // simpler product-stock-ml-per-serving derived from
                        // the serving's `ml × syrup_ratio`, with a graceful
                        // fall-through to the existing pin-unit logic).
                        const toRowUnit = (qtyInStock) => {
                          if (qtyInStock == null) return null;
                          // Path 1: Recipe-serving path (post-mix via recipe engine).
                          // Converts stock unit consumption → finished servings using
                          // the ingredient ml-per-serving from a linked recipe.
                          if (recipeServing && stockUnit?.conversion_to_base) {
                            const ingredientMlPerServing = recipeServing.syrup_ml_per_serving
                              || recipeServing.ml_of_ingredient
                              || recipeServing.ingredient_ml;
                            const stockMl = parseFloat(stockUnit.conversion_to_base);
                            if (ingredientMlPerServing && ingredientMlPerServing > 0 && stockMl > 0) {
                              return (qtyInStock * stockMl) / ingredientMlPerServing;
                            }
                          }
                          // Path 1.5: Postmix yield path — convert stock consumption →
                          // finished servings using post_mix_servings_per_unit (the
                          // effective yield stored in the product master).
                          // Count-based (e.g. Tubs, no ml on purchase unit): spu =
                          //   servings per stock unit directly (e.g. 50 servings/tub).
                          // Volume-based (e.g. Pepsi MAX concentrate): spu = servings
                          //   per purchase unit; stock qty is normalised to purchase
                          //   units first, then rescaled to the selected row unit.
                          if (prod?.is_post_mix) {
                            const spu = parseFloat(prod.post_mix_servings_per_unit);
                            if (!isNaN(spu) && spu > 0) {
                              const purchUnit = prod.units?.find(u => u.is_default_purchase_unit);
                              const purchMl = purchUnit?.conversion_to_base
                                ? parseFloat(purchUnit.conversion_to_base) : null;
                              const stkMl = stockUnit?.conversion_to_base
                                ? parseFloat(stockUnit.conversion_to_base) : null;
                              if (!purchMl || purchMl <= 0) {
                                // Count-based: spu = finished servings per stock unit
                                return qtyInStock * spu;
                              }
                              if (stkMl && stkMl > 0) {
                                // Volume-based: convert to default-serving equivalents
                                // then rescale to the auditor's selected row unit.
                                const consumptionDefaultServings =
                                  qtyInStock * (stkMl / purchMl) * spu;
                                const servingMl = prod.post_mix_serving_ml
                                  ? parseFloat(prod.post_mix_serving_ml) : null;
                                const rowUnitMl = saleUnit?.conversion_to_base
                                  ? parseFloat(saleUnit.conversion_to_base) : null;
                                if (servingMl && servingMl > 0 && rowUnitMl && rowUnitMl > 0
                                    && Math.abs(servingMl - rowUnitMl) > 0.01) {
                                  return consumptionDefaultServings * (servingMl / rowUnitMl);
                                }
                                return consumptionDefaultServings;
                              }
                            }
                          }
                          // Path 2: Standard unit-conversion path (legacy).
                          if (!saleUnit || !stockUnit || saleUnit.id === stockUnit.id) return qtyInStock;
                          const srcConv = saleUnit.conversion_to_base || 1;
                          const stkConv = stockUnit.conversion_to_base || 1;
                          if (srcConv === 0) return qtyInStock;
                          return (qtyInStock * stkConv) / srcConv;
                        };
                        const consumptionRowUnit = cons ? toRowUnit(cons.consumption_qty) : null;
                        const enteredQty = entry.quantity && !isNaN(parseFloat(entry.quantity)) ? parseFloat(entry.quantity) : null;
                        const varianceRowUnit = (enteredQty != null && consumptionRowUnit != null)
                          ? (enteredQty - consumptionRowUnit) : null;
                        const displayUnit = saleUnit?.unit_type || rowUnitLabel || stockUnitLabelRaw || '';
                        const varianceColor = varianceRowUnit == null ? 'text-slate-400'
                          : Math.abs(varianceRowUnit) < 0.01 ? 'text-green-700'
                          : Math.abs(varianceRowUnit) < 2 ? 'text-amber-600' : 'text-red-600';
                        const fmt = (v) => (v == null ? '—' : (Math.round(v * 100) / 100).toLocaleString('en-GB', { maximumFractionDigits: 2 }));
                        return (
                          <>
                            <div className="w-24 text-right" data-testid={`sale-consumption-${idx}`}>
                              {cons ? (
                                <Popover>
                                  <PopoverTrigger asChild>
                                    <button
                                      type="button"
                                      className="h-9 w-full flex items-center justify-end text-xs tabular-nums px-1 text-slate-700 hover:text-rose-700 hover:bg-rose-50 rounded transition-colors cursor-pointer underline-offset-2 hover:underline"
                                      data-testid={`sale-consumption-trigger-${idx}`}
                                      title="Click to see how this consumption was calculated"
                                    >
                                      <span>{fmt(consumptionRowUnit)}{consumptionRowUnit != null && displayUnit ? <span className="text-slate-400 ml-1">{displayUnit}</span> : null}</span>
                                    </button>
                                  </PopoverTrigger>
                                  <PopoverContent className="w-80 p-3 text-xs" align="end" data-testid={`sale-consumption-popover-${idx}`}>
                                    <div className="space-y-2">
                                      <div className="flex items-center gap-2 pb-2 border-b border-slate-200">
                                        <Calculator className="h-3.5 w-3.5 text-rose-600" />
                                        <span className="font-semibold text-slate-800">Consumption breakdown</span>
                                      </div>
                                      <p className="text-[11px] text-slate-500 leading-snug">
                                        Physical consumption in <span className="font-mono">{stockUnitLabelRaw || 'stock unit'}</span>, derived from stock movements (excludes wastage).
                                      </p>
                                      <div className="font-mono text-[11px] space-y-1">
                                        <div className="flex justify-between">
                                          <span className="text-slate-600">Opening stock</span>
                                          <span className="tabular-nums">{fmt(cons.opening_qty)}</span>
                                        </div>
                                        <div className="flex justify-between">
                                          <span className="text-slate-600">+ Deliveries</span>
                                          <span className="tabular-nums text-emerald-700">{fmt(cons.deliveries_qty)}</span>
                                        </div>
                                        <div className="flex justify-between">
                                          <span className="text-slate-600">− Credits</span>
                                          <span className="tabular-nums text-amber-700">{fmt(cons.credits_qty)}</span>
                                        </div>
                                        <div className="flex justify-between">
                                          <span className="text-slate-600">− Closing stock</span>
                                          <span className="tabular-nums text-slate-700">{fmt(cons.closing_qty)}</span>
                                        </div>
                                        <div className="flex justify-between pt-1.5 border-t border-slate-200 font-semibold">
                                          <span className="text-slate-800">= Consumption</span>
                                          <span className="tabular-nums text-rose-700">{fmt(cons.consumption_qty)} {stockUnitLabelRaw}</span>
                                        </div>
                                      </div>
                                      {/* Conversion note: postmix yield or standard unit */}
                                      {(() => {
                                        if (prod?.is_post_mix && consumptionRowUnit != null) {
                                          const spu = parseFloat(prod.post_mix_servings_per_unit);
                                          if (!isNaN(spu) && spu > 0) {
                                            const purchUnit = prod.units?.find(u => u.is_default_purchase_unit);
                                            const purchMl = purchUnit?.conversion_to_base
                                              ? parseFloat(purchUnit.conversion_to_base) : null;
                                            const isCountBased = !purchMl || purchMl <= 0;
                                            return (
                                              <div className="pt-2 border-t border-slate-200 text-[10px] text-slate-500 leading-snug">
                                                {isCountBased
                                                  ? <><span className="font-medium text-amber-700">Postmix yield:</span> {fmt(cons.consumption_qty)} {stockUnitLabelRaw} × {fmt(spu)} servings/unit = <span className="font-mono text-slate-700">{fmt(consumptionRowUnit)} {displayUnit}</span></>
                                                  : <><span className="font-medium text-amber-700">Postmix yield</span> ({fmt(spu)} spu × dilution): <span className="font-mono text-slate-700">{fmt(cons.consumption_qty)} {stockUnitLabelRaw} → {fmt(consumptionRowUnit)} {displayUnit}</span></>
                                                }
                                              </div>
                                            );
                                          }
                                        }
                                        if (saleUnit && stockUnit && saleUnit.id !== stockUnit.id) {
                                          return (
                                            <div className="pt-2 border-t border-slate-200 text-[10px] text-slate-500 leading-snug">
                                              Converted to row unit (<span className="font-mono">{displayUnit}</span>):
                                              <span className="font-mono text-slate-700 ml-1">{fmt(consumptionRowUnit)} {displayUnit}</span>
                                            </div>
                                          );
                                        }
                                        return null;
                                      })()}
                                    </div>
                                  </PopoverContent>
                                </Popover>
                              ) : (
                                <div className="h-9 flex items-center justify-end text-xs tabular-nums px-1 text-slate-400">—</div>
                              )}
                            </div>
                            <div className="w-24 text-right" data-testid={`sale-variance-${idx}`}
                              title="Entered qty − physical consumption (same unit as the row). Updates live as you type.">
                              <div className={`h-9 flex items-center justify-end text-xs tabular-nums px-1 ${varianceColor}`}>
                                <span>{varianceRowUnit == null ? '—' : (varianceRowUnit > 0 ? '+' : '') + fmt(varianceRowUnit)}</span>
                              </div>
                            </div>
                          </>
                        );
                      })()}
                      {/* P1D — PLU Investigative Allowance cell.
                          Same unit as the row's PLU sales entry.
                          Investigative only — affects EVT report,
                          not the core stocktake result. */}
                      <div className="w-24" data-testid={`sale-plu-allow-${idx}`}>
                        {pid && !isNewEmpty ? (
                          isEditable ? (
                            <div className="flex items-center gap-0.5">
                              <Input
                                type="number"
                                step="0.01"
                                min="0"
                                placeholder="--"
                                inputMode="decimal"
                                value={pluAllowances[String(pid)] || ''}
                                onChange={e => {
                                  const v = e.target.value;
                                  setPluAllowances(prev => ({ ...prev, [String(pid)]: v }));
                                }}
                                className="h-9 text-xs text-right tabular-nums w-full"
                                data-testid={`sale-plu-allow-input-${idx}`}
                                title={`PLU Investigative Allowance (${entry.unit_label || 'units'}) — EVT report only`}
                              />
                              {entry.unit_label && (
                                <span className="text-[9px] text-muted-foreground whitespace-nowrap shrink-0 pl-0.5">
                                  {entry.unit_label}
                                </span>
                              )}
                            </div>
                          ) : (
                            <div className="h-9 flex items-center justify-end text-xs tabular-nums px-1 text-muted-foreground">
                              {pluAllowances[String(pid)] && parseFloat(pluAllowances[String(pid)]) > 0
                                ? `${pluAllowances[String(pid)]} ${entry.unit_label || ''}`
                                : '—'}
                            </div>
                          )
                        ) : (
                          <div className="h-9" />
                        )}
                      </div>
                      {isEditable && (isNewEmpty || !prod) && (
                        <Button size="icon" variant="ghost" className="h-9 w-9 text-red-400 hover:text-red-600 shrink-0" onClick={() => removeRow(idx)} data-testid={`sale-remove-${idx}`}>
                          <Trash2 className="h-3.5 w-3.5" />
                        </Button>
                      )}
                      {isEditable && prod && !isNewEmpty && (
                        <Button
                          size="icon" variant="ghost"
                          className="h-9 w-9 text-rose-500 hover:text-rose-700 shrink-0"
                          onClick={() => removeRow(idx)}
                          title="Remove this sales line"
                          data-testid={`sale-remove-${idx}`}
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </Button>
                      )}
                    </div>
                    {/* iter371 Wave 2 — Mid-period price-change split UI.
                        Renders inline when this product has >1 effective
                        price segment in the audit period. The auditor
                        enters quantities per segment; the row's
                        main qty becomes the running sum and the value is
                        computed as Σ(qty_i × price_i). */}
                    {pid && (priceSegments[pid]?.length > 1) && (() => {
                      const segs = priceSegments[pid];
                      const rowSplits = splitQtys[idx] || {};
                      let totalQty = 0;
                      let totalVal = 0;
                      segs.forEach((seg, si) => {
                        const q = parseFloat(rowSplits[String(si)] || 0);
                        if (!isNaN(q) && q > 0) {
                          totalQty += q;
                          totalVal += q * Number(seg.selling_price_incl_vat || 0);
                        }
                      });
                      return (
                        <div
                          className="ml-4 mb-1 mt-1 rounded-md border border-blue-200 bg-blue-50/50 px-2 py-1.5"
                          data-testid={`sale-segments-${idx}`}
                        >
                          <div className="flex items-center justify-between gap-2 mb-1">
                            <span className="text-[10px] font-semibold text-blue-800 uppercase tracking-wide">
                              Mid-period price change — split sale by segment
                            </span>
                            <span className="text-[10px] text-blue-700 tabular-nums">
                              Total: {totalQty || 0} {entry.unit_label || ''} · {formatCurrency(totalVal)}
                            </span>
                          </div>
                          <div className="flex flex-wrap gap-2">
                            {segs.map((seg, si) => (
                              <div key={si} className="flex items-center gap-1.5">
                                <span className="text-[10px] text-slate-700">
                                  £{Number(seg.selling_price_incl_vat).toFixed(2)}
                                  <span className="text-slate-500"> ({seg.segment_from} → {seg.segment_to})</span>
                                </span>
                                <Input
                                  type="number"
                                  step="0.01"
                                  min="0"
                                  placeholder="qty"
                                  inputMode="decimal"
                                  value={rowSplits[String(si)] || ''}
                                  disabled={!isEditable}
                                  onChange={e => {
                                    const v = e.target.value;
                                    setSplitQtys(prev => {
                                      const cur = { ...(prev[idx] || {}) };
                                      cur[String(si)] = v;
                                      return { ...prev, [idx]: cur };
                                    });
                                    // Mirror sum into main quantity so the
                                    // value column + auto-save stay live.
                                    const allRows = { ...(rowSplits), [String(si)]: v };
                                    const sum = Object.values(allRows)
                                      .map(x => parseFloat(x || 0))
                                      .filter(n => !isNaN(n) && n > 0)
                                      .reduce((a, b) => a + b, 0);
                                    updateRow(idx, 'quantity', sum ? String(sum) : '');
                                  }}
                                  className="h-7 text-xs w-16 tabular-nums"
                                  data-testid={`sale-segment-qty-${idx}-${si}`}
                                />
                              </div>
                            ))}
                          </div>
                        </div>
                      );
                    })()}
                    {/* iter364 — Running balance + "Another line" footer.
                        Only rendered when this product appears on more
                        than one row (running totals are noise on
                        single-row products). */}
                    {prod && !isNewEmpty && (() => {
                      const running = runningStockByRowIdx.get(idx);
                      return (
                        <div className="flex items-center justify-end gap-3 pr-2 -mt-0.5 pb-1" data-testid={`sale-row-footer-${idx}`}>
                          {running && (
                            <span
                              className="text-[10px] text-slate-500 tabular-nums"
                              data-testid={`sale-row-running-${idx}`}
                              title={`Running total across all ${running.productCount} rows for this product, in the product's stock unit.`}
                            >
                              running: <span className="font-mono text-slate-700">
                                {(Math.round(running.running * 100) / 100).toLocaleString('en-GB', { maximumFractionDigits: 2 })}
                              </span> {running.stockUnit}
                            </span>
                          )}
                          {isEditable && (
                            <button
                              type="button"
                              onClick={() => addAnotherRowFor(idx)}
                              className="text-[10px] font-medium text-rose-600 hover:text-rose-800 hover:underline"
                              data-testid={`sale-row-another-${idx}`}
                              title="Add another sales row for the same product (e.g. different till / location)"
                            >
                              + another line
                            </button>
                          )}
                        </div>
                      );
                    })()}
                    </div>
                  );
                })}
              </div>
            ));
          })()}
          {visibleEntries.length > renderLimit && (
            <div className="pt-2 text-center">
              <Button size="sm" variant="ghost" className="text-xs text-muted-foreground"
                onClick={() => setRenderLimit(prev => prev + 100)}
                data-testid="sales-show-more">
                Showing {renderLimit} of {visibleEntries.length} — Show more
              </Button>
            </div>
          )}
        </div>

        {/* Add custom line */}
        {isEditable && (
          <div className="pt-3 border-t mt-3 flex flex-wrap items-center gap-2">
            <Button size="sm" variant="outline" onClick={addRow} data-testid="add-sale-line-btn">
              <Plus className="h-3 w-3 mr-1" />Add Product
            </Button>
            {/* iter340 v5 — Inventory master access moved to the Site
                Inventory Setup floating window in the wizard header. */}
          </div>
        )}
        <AddFromMasterModal
          open={addFromMasterOpen}
          onOpenChange={setAddFromMasterOpen}
          siteId={siteId}
          assignedProductIds={[...(siteProductIds || [])].map(Number)}
          onAssigned={handleProductAssignedFromMaster}
          offlineProducts={isOfflineMode ? (offlineData?.products || []) : null}
          onOfflineAssign={isOfflineMode ? handleOfflineMasterAssign : null}
        />
      </CardContent>
    </Card>
  );
});
